/**
 * High-Precision 3D Moon Globe
 * Features NASA LROC photographic albedo, LOLA elevation displacement,
 * custom LOLA false-color hypsometric tinting, and dynamic LOD management.
 */

import * as THREE from 'three';
import { LOLATopographyShader } from './Shaders.js';

export class MoonGlobe {
  constructor(radius = 5.0, maxAnisotropy = 16) {
    this.radius = radius;
    this.maxAnisotropy = maxAnisotropy;
    this.group = new THREE.Group();
    this.group.name = 'MoonGlobe';

    this.currentMode = 0; // 0: Albedo, 1: LOLA Topography, 2: Shaded Relief
    this.sunDirection = new THREE.Vector3(0.6, 0.3, 2.2).normalize();
    this.sunColor = new THREE.Vector3(1.0, 0.98, 0.95);
    this.ambientColor = new THREE.Vector3(0.04, 0.05, 0.08); // Earthshine tone

    this.textureLoader = new THREE.TextureLoader();
    this.initGlobe();
  }

  initGlobe() {
    // 128x128 tessellation provides smooth sphere curvature with minimal GPU overhead
    this.geometry = new THREE.SphereGeometry(this.radius, 128, 128);

    // Load textures
    this.albedoTexture = this.textureLoader.load('./assets/textures/moon_color_2k.jpg', (tex) => {
      tex.anisotropy = this.maxAnisotropy;
      tex.colorSpace = THREE.SRGBColorSpace;
      if (this.shaderMaterial) {
        this.shaderMaterial.uniforms.tAlbedo.value = tex;
        this.shaderMaterial.needsUpdate = true;
      }
    });

    this.displacementTexture = this.textureLoader.load('./assets/textures/moon_displacement.jpg', (tex) => {
      tex.anisotropy = this.maxAnisotropy;
      if (this.shaderMaterial) {
        this.shaderMaterial.uniforms.tDisplacement.value = tex;
        this.shaderMaterial.needsUpdate = true;
      }
    });

    // Custom Selenological Shader Material
    this.shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tAlbedo: { value: this.albedoTexture },
        tDisplacement: { value: this.displacementTexture },
        uSunDirection: { value: this.sunDirection },
        uSunColor: { value: this.sunColor },
        uAmbientColor: { value: this.ambientColor },
        uMode: { value: this.currentMode }
      },
      vertexShader: LOLATopographyShader.vertexShader,
      fragmentShader: LOLATopographyShader.fragmentShader
    });

    this.mesh = new THREE.Mesh(this.geometry, this.shaderMaterial);
    // Orient so 0° lon, 0° lat (Sinus Medii) faces the initial camera along +Z
    this.mesh.rotation.y = -Math.PI / 2;
    this.group.add(this.mesh);
  }

  setLayerMode(mode) {
    // mode: 0 (Albedo), 1 (LOLA Topography), 2 (Shaded Relief)
    this.currentMode = mode;
    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.uMode.value = mode;
    }
  }

  setSunPosition(direction, isStudioLight = false) {
    this.sunDirection.copy(direction).normalize();
    if (isStudioLight) {
      // Uniform inspection lighting (high ambient, gentle diffuse)
      this.shaderMaterial.uniforms.uAmbientColor.value.set(0.65, 0.65, 0.70);
      this.shaderMaterial.uniforms.uSunColor.value.set(0.60, 0.60, 0.60);
    } else {
      // Realistic high-contrast space lighting with subtle Earthshine
      this.shaderMaterial.uniforms.uAmbientColor.value.set(0.04, 0.05, 0.08);
      this.shaderMaterial.uniforms.uSunColor.value.set(1.0, 0.98, 0.95);
    }
    this.shaderMaterial.uniforms.uSunDirection.value.copy(this.sunDirection);
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.shaderMaterial) this.shaderMaterial.dispose();
    if (this.albedoTexture) this.albedoTexture.dispose();
    if (this.displacementTexture) this.displacementTexture.dispose();
  }
}
